import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { ApiServiceService } from '../../services/api-service.service';
import { environment } from '../../../environments/environment.development';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { DeliveryShiprocketService } from '../../services/delivery-shiprocket.service';
import { MatDialog } from '@angular/material/dialog';
import { MyOrderCancelModelComponent } from '../my-order-cancel-model/my-order-cancel-model.component';
import { LoaderService } from '../../services/loader.service';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { OrderReturnComponent } from '../order-return/order-return.component';

interface Order {
  _id: string;
  id: string;
  order_status: string;
  created_at?: string;
  createdAt?: string;
  items: OrderItem[];
  name: string;
  address: string;
  city: string;
  pincode: string;
  state: string;
  country: string;
  email: string;
  mobile: string;
  payment_method: string;
  shipping_charges: number;
  coupon_amount: number;
  grand_total: number;
  orders_products: any[];
}

interface OrderItem {
  product_id: {
    images: string[];
    variants: { color: string; size: string }[];
  };
  name: string;
  price: number;
  quantity: number;
  createdAt: string;
  product_name: string;
  product_qty: number;
  product_price: number;
}

@Component({
  selector: 'app-myorder-list',
  templateUrl: './myorder-list.component.html',
  styleUrls: ['./myorder-list.component.scss'],
})
export class MyorderListComponent implements OnInit, OnDestroy {
  userOrder: Order[] = [];
  mycartListImageUrl = environment.ImageBaseUrl;
  filteredOrders: Order[] = [];
  currentFilter: string = '';

  // Infinite scroll properties
  currentPage = 1;
  itemsPerPage = 10;
  isLoadingMore = false;
  hasMoreItems = true;
  private scrollSubject = new Subject<Event>();
  private subscriptions: Subscription[] = [];

  selectedMonth: string = '';
  selectedYear: string = '';
  activeMenu: string | null = null;
  customeOrder: any;

  get orderYears(): number[] {
    const currentYear = new Date().getFullYear();
    return Array.from(
      { length: currentYear - 2000 + 1 },
      (_, index) => currentYear - index,
    );
  }

  private getOrderDate(order: Order): Date {
    return new Date(order.createdAt || order.created_at || '');
  }

  constructor(
    private api: ApiServiceService,
    private router: Router,
    private toastr: ToastrService,
    private deliveryApi: DeliveryShiprocketService,
    public dialog: MatDialog,
    private loader: LoaderService,
  ) {}

  ngOnInit() {
    this.setupScrollListener();
    this.getOrdersList();
  }

  ngAfterViewInit() {
    this.customOrder();
  }

  ngOnDestroy() {
    this.scrollSubject.complete();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  setupScrollListener() {
    const scrollSub = this.scrollSubject
      .pipe(debounceTime(200))
      .subscribe((event) => this.onScroll(event));
    this.subscriptions.push(scrollSub);
  }

  getOrdersList() {
    // this.loader.showLoader();
    const sub = this.api
      .getOrdersList(this.currentPage, this.itemsPerPage)
      .subscribe({
        next: (res: any) => {
          // this.loader.hideLoader();

          if (res.status === 403) {
            this.router.navigate(['/Signin']);
            this.toastr.error('Please Login First To Access Orders');
            localStorage.clear();
          } else {
            const newOrders = res.data || [];

            if (this.currentPage === 1) {
              this.userOrder = newOrders;
              this.filteredOrders = [...newOrders];
            } else {
              this.userOrder = [...this.userOrder, ...newOrders];
              this.filteredOrders = [...this.userOrder];
            }

            this.customeOrder = res.data?.[0] || {};
            this.hasMoreItems = newOrders.length === this.itemsPerPage;
          }
        },
        error: (err) => {
          this.loader.hideLoader();
          this.toastr.error('Failed to load orders');
          console.error('Error loading orders:', err);
        },
      });
    this.subscriptions.push(sub);
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(event: Event) {
    this.scrollSubject.next(event);
  }

  onScroll(event: Event): void {
    if (this.isLoadingMore || !this.hasMoreItems) return;

    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollPosition =
      window.scrollY ||
      window.pageYOffset ||
      document.body.scrollTop +
        ((document.documentElement && document.documentElement.scrollTop) || 0);

    if (documentHeight - (scrollPosition + windowHeight) < 200) {
      this.loadMoreOrders();
    }
  }

  loadMoreOrders() {
    if (this.isLoadingMore || !this.hasMoreItems) return;

    this.isLoadingMore = true;
    this.currentPage++;

    const sub = this.api
      .getOrdersList(this.currentPage, this.itemsPerPage)
      .subscribe({
        next: (res: any) => {
          const newOrders = res.data || [];
          this.userOrder = [...this.userOrder, ...newOrders];
          this.filteredOrders = [...this.userOrder];
          console.log('this.filter-->', this.filteredOrders);
          this.hasMoreItems = newOrders.length === this.itemsPerPage;
          this.isLoadingMore = false;
        },
        error: () => {
          this.isLoadingMore = false;
          this.currentPage--; // Revert page increment on error
        },
      });
    this.subscriptions.push(sub);
  }

  isOrderOlderThan7Days(orderDate: string): boolean {
    const currentDate = new Date();
    const orderCreationDate = new Date(orderDate);

    // Calculate the difference in milliseconds
    const timeDifference = currentDate.getTime() - orderCreationDate.getTime();

    // Convert milliseconds to days
    const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

    // Return true if the order is older than 7 days
    return daysDifference > 7;
  }

  toggleMenu(orderId: string): void {
    this.activeMenu = this.activeMenu === orderId ? null : orderId;
  }

  initiateReturn(orderId: string): void {
    console.log('Initiate return for order:', orderId);
    // Implement your return logic here
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      !(event.target as HTMLElement).closest('.relative.inline-block.text-left')
    ) {
      this.activeMenu = null;
    }
  }

  getProductById(id: string): void {
    this.router.navigate(['order-details', id]);
  }

  // trackOrder(waybill: string) {
  //   this.router.navigate(['track-order'], {
  //     queryParams: { waybill }
  //   });
  // }
  trackOrder(waybill: string, products: any) {
    const data = [
      {
        productName: products.name,
        productImage: products?.images,
      },
    ];
    this.router.navigate(['track-order'], {
      queryParams: {
        waybill,
        products: JSON.stringify(data), // convert array to string
      },
    });
  }

  trackByOrderId(index: number, order: Order): string {
    return order._id;
  }

  trackByProductId(index: number, product: OrderItem): string {
    return product.product_id?.images?.[0] || index.toString();
  }

  openDialog(id: string): void {
    const dialogRef = this.dialog.open(MyOrderCancelModelComponent, {
      width: '50%',
      data: { id },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        this.getOrdersList(); // Refresh orders if cancellation was successful
      }
    });
  }

  retunOrder(id: any) {
    this.router.navigate(['returnOrder', id]);
  }

  customOrder() {
    if (!this.customeOrder?.orders_products?.[0]) return;

    const para = {
      order_id: this.customeOrder.orders_products[0].order_id,
      order_date: this.customeOrder.orders_products[0].created_at,
      billing_customer_name: this.customeOrder.name,
      billing_last_name: this.customeOrder.name,
      billing_address: this.customeOrder.address,
      billing_address_2: this.customeOrder.address,
      billing_city: this.customeOrder.city,
      billing_pincode: this.customeOrder.pincode,
      billing_state: this.customeOrder.state,
      billing_country: this.customeOrder.country,
      billing_email: this.customeOrder.email,
      billing_phone: this.customeOrder.mobile,
      billing_alternate_phone: this.customeOrder.mobile,
      shipping_is_billing: true,
      shipping_customer_name: this.customeOrder.name,
      shipping_address: this.customeOrder.address,
      shipping_address_2: this.customeOrder.address,
      shipping_city: this.customeOrder.city,
      shipping_pincode: this.customeOrder.pincode,
      shipping_country: this.customeOrder.country,
      shipping_state: this.customeOrder.state,
      shipping_email: this.customeOrder.email,
      shipping_phone: this.customeOrder.mobile,
      order_items: [
        {
          name: this.customeOrder.orders_products[0].product_name,
          sku: '1',
          units: this.customeOrder.orders_products[0].product_qty,
          selling_price: this.customeOrder.orders_products[0].product_price,
          discount: this.customeOrder.coupon_amount,
        },
      ],
      payment_method: this.customeOrder.payment_method,
      shipping_charges: this.customeOrder.shipping_charges,
      total_discount: this.customeOrder.coupon_amount,
      sub_total: this.customeOrder.grand_total,
      length: this.customeOrder.orders_products.length,
      breadth: '1',
      height: '1',
      weight: '0.5',
      ewaybill_no: 'sna12',
      invoice_number: this.customeOrder.orders_products[0].id,
    };

    const sub = this.deliveryApi.createCustomOrder(para).subscribe({
      next: (res: any) => {
        console.log('Shiprocket order created:', res);
      },
      error: (err) => {
        console.error('Error creating Shiprocket order:', err);
      },
    });
    this.subscriptions.push(sub);
  }

  filterOrders(status: string): void {
    let filteredByStatus = [...this.filteredOrders];

    if (
      status === 'New' ||
      status === 'Cancelled' ||
      status === 'Completed' ||
      status === 'Pending'
    ) {
      filteredByStatus = filteredByStatus.filter(
        (order) => order.order_status === status,
      );
    }

    if (this.selectedMonth) {
      filteredByStatus = filteredByStatus.filter(
        (order) =>
          this.getOrderDate(order).getMonth() + 1 ===
          parseInt(this.selectedMonth),
      );
    }

    if (this.selectedYear) {
      filteredByStatus = filteredByStatus.filter(
        (order) =>
          this.getOrderDate(order).getFullYear() ===
          parseInt(this.selectedYear),
      );
    }

    this.userOrder = filteredByStatus;
  }

  selectFilter(event: any) {
    const status = event.target.value;
    if (status === 'All') {
      this.resetFilters();
    } else {
      this.currentFilter = status;
      this.filterOrders(status);
    }
  }

  selectMonth(event: any) {
    this.selectedMonth = event.target.value;
    this.filterOrders(this.currentFilter);
  }

  selectYear(event: any) {
    this.selectedYear = event.target.value === 'All' ? '' : event.target.value;
    this.filterOrders(this.currentFilter);
  }

  resetFilters() {
    this.currentFilter = '';
    this.selectedMonth = '';
    this.selectedYear = '';
    this.userOrder = [...this.filteredOrders];

    const selectElements = document.querySelectorAll('select');
    selectElements.forEach((select) => {
      select.value = 'All';
    });
  }
}

// import { Component, HostListener } from '@angular/core';
// import { ApiServiceService } from '../../services/api-service.service';
// import { environment } from '../../../environments/environment.development';
// import { Router } from '@angular/router';
// import { ToastrService } from 'ngx-toastr';
// import { DeliveryShiprocketService } from '../../services/delivery-shiprocket.service';
// import { MatDialog } from '@angular/material/dialog';
// import { MyOrderCancelModelComponent } from '../my-order-cancel-model/my-order-cancel-model.component';
// import { LoaderService } from '../../services/loader.service';
// import { debounceTime, Subject } from 'rxjs';

// @Component({
//   selector: 'app-myorder-list',
//   templateUrl: './myorder-list.component.html',
//   styleUrl: './myorder-list.component.scss'
// })
// export class MyorderListComponent {

//   userOrder : any[]=[];
//   mycartListImageUrl = environment.ImageBaseUrl;
//   orderdetails: any;
//   order: any; // Holds the order details
//   productdetail: any;
//   customeOrder: any;

//   filteredOrders: any[] = [];
//   currentFilter: string = '';

//   // Infinite scroll properties
//   currentPage = 1;
//   itemsPerPage = 10;
//   isLoadingMore = false;
//   hasMoreItems = true;
//   private scrollSubject = new Subject<Event>();

//   selectedMonth: string = '';
//   selectedYear: string = '';
//   constructor(private api:ApiServiceService , private Router:Router , private toastr: ToastrService ,
//     private  deliveryApi: DeliveryShiprocketService  ,public dialog: MatDialog
// ,private Loader:LoaderService  ){}

//   ngAfterViewInit(){

//     this.getOrdersList()
//     this.customOrder()
//   }

//   setupScrollListener() {
//     this.scrollSubject.pipe(
//       debounceTime(200)
//     ).subscribe(event => this.onScroll(event));
//   }

//   getOrdersList() {
//     this.Loader.showLoader();
//     this.api.getOrdersList(this.currentPage, this.itemsPerPage).subscribe((res: any) => {
//       this.Loader.hideLoader();

//       if (res.status === 403) {
//         this.Router.navigate(['/Signin']);
//         this.toastr.error('Please Login First To Access Orders');
//         localStorage.clear();
//       } else {
//         const newOrders = res.data;

//         if (this.currentPage === 1) {
//           this.userOrder = newOrders;
//         } else {
//           this.userOrder = [...this.userOrder, ...newOrders];
//         }

//         this.customeOrder = res.data;
//         this.filteredOrders = [...this.userOrder];
//         this.hasMoreItems = newOrders.length === this.itemsPerPage;
//       }
//     });
//   }

//   @HostListener('window:scroll', ['$event'])
//   onWindowScroll(event: Event) {
//     this.scrollSubject.next(event);
//   }

//   onScroll(event: Event): void {
//     if (this.isLoadingMore || !this.hasMoreItems) return;

//     const windowHeight = window.innerHeight;
//     const documentHeight = document.documentElement.scrollHeight;
//     const scrollPosition = window.scrollY || window.pageYOffset ||
//                          document.body.scrollTop + (document.documentElement && document.documentElement.scrollTop || 0);

//     if (documentHeight - (scrollPosition + windowHeight) < 200) {
//       this.loadMoreOrders();
//     }
//   }

//   loadMoreOrders() {
//     if (this.isLoadingMore || !this.hasMoreItems) return;

//     this.isLoadingMore = true;
//     this.currentPage++;

//     this.api.getOrdersList(this.currentPage, this.itemsPerPage).subscribe(
//       (res: any) => {
//         const newOrders = res.data;
//         this.userOrder = [...this.userOrder, ...newOrders];
//         this.filteredOrders = [...this.userOrder];
//         this.isLoadingMore = false;
//         this.hasMoreItems = newOrders.length === this.itemsPerPage;
//       },
//       () => {
//         this.isLoadingMore = false;
//       }
//     );
//   }

// // // Cancel an item in the order
// // cancelOrderItem(itemId: string): void {
// //   if (!confirm('Are you sure you want to cancel this item?')) {
// //     return; // Exit if the user cancels the confirmation dialog
// //   }

// //   this.api.cancelOrderItem(this.customeOrder, itemId).subscribe(
// //     (response: any) => {
// //       this.toastr.success(response.message);
// //       this.getOrdersList(); // Refresh the order details after cancellation
// //     },
// //     (error) => {
// //       this.toastr.error(error.error?.message || 'Failed to cancel item');

// //     }
// //   );
// // }

// canReturn(): boolean {

//   const currentDate = new Date();
//   const orderDate = new Date(this.customeOrder.created_at);
//   const diffTime = Math.abs(currentDate.getTime() - orderDate.getTime());
//   const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

//   return diffDays <= 7;
// }

// // Add to your component class
// activeMenu: string | null = null;

// toggleMenu(orderId: string): void {
//     this.activeMenu = this.activeMenu === orderId ? null : orderId;
// }

// initiateReturn(orderId: string): void {
//     // Implement your return logic here
//     console.log('Initiate return for order:', orderId);
//     // You might want to navigate to a return page or open a dialog
// }

// // Also add this to handle clicks outside the menu
// @HostListener('document:click', ['$event'])
// onDocumentClick(event: MouseEvent): void {
//     if (!(event.target as HTMLElement).closest('.relative.inline-block.text-left')) {
//         this.activeMenu = null;
//     }
// }

// getProductById(id: any){
//         this.Router.navigate(["order-details", id])
//     }

//   // productDetails(data: any){
//   //   // console.log("data",data.id)
//   //
//   treckOrder(id: any){
//     // console.log('id',id)
//     this.Router.navigate(["order-cancelled", id])
//   }

// cancelOrderId(id: any){
//   // console.log('id',id)
//   this.Router.navigate(["my-order-cancel-model", id])
// }

// openDialog(id: any): void {
//   const dialogRef = this.dialog.open(MyOrderCancelModelComponent, {
//     width: '50%',
//     data: { id }
//   });

//   dialogRef.afterClosed().subscribe(result => {
//     // console.log('The dialog was closed');
//   });
// }

//   customOrder(){
//     const para ={
//       // this.customeOrder.orders_products.created_at
//         "order_id": this.customeOrder.orders_products[0].order_id,
//         "order_date": this.customeOrder.orders_products[0].created_at,
//         // "pickup_location": "sehore",
//         // "channel_id": "234",
//         // "comment": "gelolo",
//         // "reseller_name": "shubham",
//         // "company_name": "shubham",
//         "billing_customer_name": this.customeOrder.name,
//         "billing_last_name": this.customeOrder.name,
//         "billing_address": this.customeOrder.address,
//         "billing_address_2": this.customeOrder.address,
//         // "billing_isd_code": "123",
//         "billing_city": this.customeOrder.city,
//         "billing_pincode": this.customeOrder.pincode,
//         "billing_state": this.customeOrder.state,
//         "billing_country": this.customeOrder.country,
//         "billing_email": this.customeOrder.email,
//         "billing_phone": this.customeOrder.mobile,
//         "billing_alternate_phone": this.customeOrder.mobile,
//         "shipping_is_billing": true,
//         "shipping_customer_name": this.customeOrder.name,
//         // "shipping_last_name": "NA",
//         "shipping_address": this.customeOrder.address,
//         "shipping_address_2": this.customeOrder.address,
//         "shipping_city": this.customeOrder.city,
//         "shipping_pincode": this.customeOrder.pincode ,
//         "shipping_country": this.customeOrder.country,
//         "shipping_state":this.customeOrder.state ,
//         "shipping_email":this.customeOrder.email ,
//         "shipping_phone": this.customeOrder.mobile,
//         "order_items": [
//             {
//                 "name": this.customeOrder.orders_products[0].product_name,
//                 "sku": "1",
//                 "units": this.customeOrder.orders_products[0].product_qty,
//                 "selling_price" : this.customeOrder.orders_products[0].product_price ,
//                 "discount": this.customeOrder.coupon_amount,
//                 // "tax": "NA",
//                 // "hsn": "NA"
//             }
//         ],
//         "payment_method": this.customeOrder.payment_method,
//         "shipping_charges":  this.customeOrder.shipping_charges,
//         // "giftwrap_charges": "NA",
//         // "transaction_charges": "NA",
//         "total_discount": this.customeOrder.coupon_amount,
//         "sub_total": this.customeOrder.grand_total,
//         "length": this.customeOrder.orders_products.length,
//         "breadth": "1",
//          "height": "1",
//         "weight": "0.5",
//          "ewaybill_no": "sna12",
//         //  "customer_gstin": "",
//         "invoice_number": this.customeOrder.orders_products.id,
//         // "order_type":"NA",

//     }
//     // console.log('order parameter' , para)
//     this.deliveryApi.createCustomOrder(para).subscribe((res: any)=>{
//       // console.log('shiprocket courier coustom order' , res)
//       // this.estimatedDeliveryDate = res.data.available_courier_companies[0].etd

//     })

//   }

//   filterOrders(status: string): void {
//     let filteredByStatus = this.filteredOrders;

//     // Filter by order status if it's not 'All'
//     if (status === 'New' || status === 'Cancelled' || status === 'Completed' || status === 'Pending') {
//       filteredByStatus = filteredByStatus.filter(order => order.order_status === status);
//     }

//     // Filter by selected month
//     if (this.selectedMonth) {
//       filteredByStatus = filteredByStatus.filter(order => new Date(order.createdAt).getMonth() + 1 === parseInt(this.selectedMonth));
//     }

//     // Filter by selected year
//     if (this.selectedYear) {
//       filteredByStatus = filteredByStatus.filter(order => new Date(order.createdAt).getFullYear() === parseInt(this.selectedYear));
//     }

//     this.userOrder = filteredByStatus; // Update displayed orders
//     // console.log('Filtered orders:', this.userOrder);
//   }

//   selectFilter(event: any) {
//     const status = event.target.value;
//     if (status === 'All') {
//       this.resetFilters(); // Call resetFilters if "All" is selected
//     } else {
//       this.currentFilter = status; // Set the current filter status
//       this.filterOrders(status); // Filter orders based on status
//     }
//   }

//   // Update month selection and filter orders
//   selectMonth(event: any) {
//     this.selectedMonth = event.target.value; // Get the selected month
//     this.filterOrders(this.currentFilter); // Filter orders with the current status and new month
//   }

//   // Update year selection and filter orders
//   selectYear(event: any) {
//     this.selectedYear = event.target.value; // Get the selected year
//     this.filterOrders(this.currentFilter); // Filter orders with the current status and new year
//   }

//   // Reset to show all orders
//   resetFilters() {
//     this.currentFilter = ''; // Clear current filter
//     this.selectedMonth = ''; // Clear selected month
//     this.selectedYear = ''; // Clear selected year
//     this.userOrder = [...this.filteredOrders]; // Reset to all orders

//     const selectElements = document.querySelectorAll('select');
//     selectElements.forEach(select => {
//         select.value = 'All';
//     });
//   }

// }
